import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Brain, Book, Layout, 
  ArrowDown, Coffee, Bug, Code, Users,
  Github, LineChart, GitBranch, Star,
  Mail, Globe, Clock, Target // Add missing imports
} from 'lucide-react';
import styles from './AboutPage.module.css';

const GITHUB_USERS = ['MohanRamSridhar', 'soorajsunil1409', 'Etrlnx'];

const SectionTitle = ({ children, visible }) => (
  <div className={`${styles.sectionTitle} ${visible ? styles.titleVisible : ''}`}>
    <h2>{children}</h2>
    <div className={styles.titleUnderline} />
  </div>
);

const DevStats = () => (
  <div className={styles.devStats}>
    <div className={styles.statItem}>
      <Coffee className={styles.statIcon} />
      <span>IDK how many RedBulls</span>
    </div>
    <div className={styles.statItem}>
      <Bug className={styles.statIcon} />
      <span>∞ Bugs Fixed</span>
    </div>
    <div className={styles.statItem}>
      <Code className={styles.statIcon} />
      <span>1000+ Lines of Code</span>
    </div>
  </div>
);

const ProjectOverview = () => (
  <div className={styles.overview}>
    <div className={styles.overviewStats}>
      <div className={styles.statCard}>
        <LineChart size={32} className={styles.statIcon} />
        <h3>2+</h3>
        <p>Active Users</p>
      </div>
      <div className={styles.statCard}>
        <Clock size={32} className={styles.statIcon} />
        <h3>24/7</h3>
        <p>Availability</p>
      </div>
      <div className={styles.statCard}>
        <Target size={32} className={styles.statIcon} />
        <h3>95%</h3>
        <p>Accuracy Rate</p>
      </div>
      <div className={styles.statCard}>
        <GitBranch size={32} className={styles.statIcon} />
        <h3>500+</h3>
        <p>Commits</p>
      </div>
      <div className={styles.statCard}>
        <Star size={32} className={styles.statIcon} />
        <h3>4.5/5</h3>
        <p>User Rating</p>
      </div>
    </div>
  </div>
);

const ContactSection = () => (
  <div className={styles.contact}>
    <h3>Get in Touch</h3>
    <div className={styles.contactLinks}>
      <a href="mailto:team@studyai.com" className={styles.contactLink}>
        <Mail size={20} /> Email Us
      </a>
      <a href="https://studyai.com" className={styles.contactLink}>
        <Globe size={20} /> Visit Website
      </a>
      <a href="https://github.com/studyai" className={styles.contactLink}>
        <Github size={20} /> GitHub
      </a>
    </div>
  </div>
);

const AboutPage = () => {
  const [developers, setDevelopers] = useState([]);
  const [visibleSections, setVisibleSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sectionsRef = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => [...prev, entry.target.id]);
          }
        });
      },
      { threshold: 0.3 }
    );

    sectionsRef.current.forEach(section => {
      if (section) observer.observe(section);
    });

    const fetchDevelopers = async () => {
      setLoading(true);
      try {
        const devProfiles = await Promise.all(
          GITHUB_USERS.map(async (username) => {
            const response = await fetch(`https://api.github.com/users/${username}`);
            if (!response.ok) throw new Error('GitHub API error');
            return response.json();
          })
        );
        setDevelopers(devProfiles);
      } catch (error) {
        console.error('Error fetching developer profiles:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDevelopers();
    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: <Brain size={32} />,
      title: 'AI-Powered Learning',
      description: 'Personalized study materials generated by Google Gemini AI'
    },
    {
      icon: <Book size={32} />,
      title: 'Interactive Study',
      description: 'Dynamic quiz cards for enhanced learning retention'
    },
    {
      icon: <Layout size={32} />,
      title: 'Modern Interface',
      description: 'Clean, intuitive design for seamless learning experience'
    },
  ];

  return (
    <div className={styles.container}>
      <nav className={styles.nav}>
        <button onClick={() => navigate('/')} className={styles.backButton}>
          <ArrowLeft size={20} /> Back to Home
        </button>
      </nav>

      <section 
        ref={el => sectionsRef.current[0] = el}
        id="hero"
        className={`${styles.section} ${styles.hero}`}
      >
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>StudyAI</h1>
          <p className={styles.heroSubtitle}>
            Transform Your Learning Experience
          </p>
          <p className={styles.heroDescription}>
            Harness the power of AI to create personalized study materials
            and interactive learning experiences.
          </p>
          <DevStats />
        </div>
        <ArrowDown className={styles.scrollIndicator} size={32} />
      </section>

      <section 
        ref={el => sectionsRef.current[1] = el}
        id="overview"
        className={`${styles.section} ${visibleSections.includes('overview') ? styles.visible : ''}`}
      >
        <SectionTitle visible={visibleSections.includes('overview')}>
          Project Overview
        </SectionTitle>
        <ProjectOverview />
      </section>

      <section 
        ref={el => sectionsRef.current[2] = el}
        id="features"
        className={`${styles.section} ${visibleSections.includes('features') ? styles.visible : ''}`}
      >
        <SectionTitle visible={visibleSections.includes('features')}>
          Key Features
        </SectionTitle>
        <div className={styles.featureGrid}>
          {features.map((feature, index) => (
            <div 
              key={feature.title}
              className={styles.feature}
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className={styles.featureIcon}>{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section 
        ref={el => sectionsRef.current[3] = el}
        id="tech"
        className={`${styles.section} ${visibleSections.includes('tech') ? styles.visible : ''}`}
      >
        <SectionTitle visible={visibleSections.includes('tech')}>
          Tech Stack
        </SectionTitle>
        <div className={styles.techStack}>
          <div className={styles.techLayer}>
            <h3>Frontend</h3>
            <div className={styles.techItems}>
              <span>React 18</span>
              <span>CSS Modules</span>
              <span>Lucide Icons</span>
            </div>
          </div>
          <div className={styles.techLayer}>
            <h3>Backend</h3>
            <div className={styles.techItems}>
              <span>Node.js</span>
              <span>Express</span>
              <span>Google Gemini AI</span>
            </div>
          </div>
        </div>
      </section>

      <section 
        ref={el => sectionsRef.current[4] = el}
        id="team"
        className={`${styles.section} ${visibleSections.includes('team') ? styles.visible : ''}`}
      >
        <SectionTitle visible={visibleSections.includes('team')}>
          The Dream Team
        </SectionTitle>
        {loading ? (
          <div className={styles.loading}>Loading team profiles...</div>
        ) : error ? (
          <div className={styles.error}>{error}</div>
        ) : (
          <div className={styles.teamGrid}>
            {developers.map((dev, index) => (
              <div 
                key={dev.login || index}
                className={styles.teamMember}
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className={styles.imageWrapper}>
                  <img 
                    src={dev.avatar_url} 
                    alt={dev.name || dev.login}
                    onError={(e) => {
                      e.target.src = 'https://github.com/github.png';
                    }}
                  />
                </div>
                <h3>{dev.name || dev.login}</h3>
                {dev.bio && <p>{dev.bio}</p>}
                <a 
                  href={dev.html_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={styles.githubLink}
                >
                  <Github size={20} /> GitHub Profile
                </a>
              </div>
            ))}
          </div>
        )}
      </section>

      <section 
        ref={el => sectionsRef.current[5] = el}
        id="contact"
        className={`${styles.section} ${visibleSections.includes('contact') ? styles.visible : ''}`}
      >
        <SectionTitle visible={visibleSections.includes('contact')}>
          Connect With Us
        </SectionTitle>
        <ContactSection />
      </section>
    </div>
  );
};

export default AboutPage;